/**
 * Document style versions — high-level visual templates for invoices/receipts.
 * Tokens drive preview HTML and are stored on ColumnSettings.documentStyleVersion.
 * Selecting a style also applies sensible ColumnSettings color defaults so PDF
 * generators that already read those fields pick up the theme.
 */

export const DOCUMENT_STYLE_VERSIONS = {
    classic: {
        id: 'classic',
        nameKey: 'DocumentStyleClassic',
        nameFallback: 'Xtram Classic',
        descriptionKey: 'DocumentStyleClassicDesc',
        descriptionFallback: 'העיצוב הנוכחי עם כותרת עליונה וטבלת פריטים כחולה',
        previewAccent: '#304FFF',
        previewSecondary: '#F9AA2A',
        layout: 'classic',
        tableHeaderMode: 'softBlue', // light blue bg, blue text
        totalsMode: 'horizontalBar',
        defaults: {
            titleTextColor: '#000000',
            companyInfoTextColor: '#000000',
            clientDetailsTextColor: '#000000',
            tableTextColor: '#000000',
            totalsTextColor: '#000000',
            accentColor: '#304FFF',
            upperPanelColor: '#fbfbfb',
            textColor: '#000000',
            backgroundColor: '#ffffff',
            svgImageColor: '#F9AA2A',
            logoPosition: 'left',
            totalsLayout: 'horizontal',
        },
        theme: {
            accent: '#304FFF',
            accentSoft: '#E8EEFF',
            // Document title stays black in classic (matches original .haktsaa1)
            title: '#000000',
            tableHeaderBg: '#E8EEFF',
            tableHeaderColor: '#304FFF',
            zebraRow: '#f8f9fa',
            divider: '#cccccc',
            totalHighlightBg: '#162473',
            totalHighlightColor: '#ffffff',
            bannerBg: null,
        },
    },
    greenMinimal: {
        id: 'greenMinimal',
        nameKey: 'DocumentStyleGreenMinimal',
        nameFallback: 'Xtram Fresh',
        descriptionKey: 'DocumentStyleGreenMinimalDesc',
        descriptionFallback: 'כותרות ירוקות, לוגו משמאל, טבלה מפוספסת',
        previewAccent: '#7ab00e ',
        previewSecondary: '#2d2d2d',
        layout: 'splitLogoLeft',
        tableHeaderMode: 'accentText', // lime-green text headers, no fill (like sample invoice)
        totalsMode: 'stackRight',
        defaults: {
            titleTextColor: '#7ab00e ',
            companyInfoTextColor: '#000000',
            clientDetailsTextColor: '#000000',
            tableTextColor: '#000000',
            totalsTextColor: '#000000',
            accentColor: '#7ab00e',
            upperPanelColor: '#ffffff',
            textColor: '#000000',
            backgroundColor: '#ffffff',
            svgImageColor: '#7ab00e ',
            logoPosition: 'left',
            totalsLayout: 'vertical',
        },
        theme: {
            accent: '#7ab00e ',
            accentSoft: '#f2f2f2',
            title: '#7ab00e ',
            // Right-side header texts (company name, כותרת ימין, לכבוד) — slightly darker green
            headerRightColor: '#7ab00e',
            tableHeaderBg: 'transparent',
            tableHeaderColor: '#7ab00e ',
            // Alternating white / light-gray rows (sample invoice zebra)
            zebraRow: '#f2f2f2',
            zebraRowAlt: '#ffffff',
            divider: '#222222',
            totalHighlightBg: 'transparent',
            totalHighlightColor: '#000000',
            bannerBg: null,
        },
    },
    greenProfessional: {
        id: 'greenProfessional',
        nameKey: 'DocumentStyleGreenProfessional',
        nameFallback: 'Xtram Pro',
        descriptionKey: 'DocumentStyleGreenProfessionalDesc',
        descriptionFallback: 'כותרות טבלה ירוקות מלאות ותיבת סה״כ מודגשת',
        previewAccent: '#0A9651 ',
        previewSecondary: '#1b6b32',
        layout: 'splitLogoLeft',
        tableHeaderMode: 'solidAccent', // solid green header, white text
        totalsMode: 'highlightBox',
        defaults: {
            titleTextColor: '#0A9651 ',
            companyInfoTextColor: '#000000',
            clientDetailsTextColor: '#000000',
            tableTextColor: '#000000',
            totalsTextColor: '#000000',
            accentColor: '#0A9651',
            upperPanelColor: '#ffffff',
            textColor: '#222222',
            backgroundColor: '#ffffff',
            svgImageColor: '#0A9651 ',
            logoPosition: 'left',
            totalsLayout: 'vertical',
        },
        theme: {
            accent: '#0A9651 ',
            accentSoft: '#e8f6ec',
            title: '#0A9651 ',
            tableHeaderBg: '#0A9651 ',
            tableHeaderColor: '#ffffff',
            zebraRow: '#f3f3f3',
            divider: '#0A9651 ',
            totalHighlightBg: '#0A9651 ',
            totalHighlightColor: '#ffffff',
            bannerBg: null,
        },
    },
    corporateBlue: {
        id: 'corporateBlue',
        nameKey: 'DocumentStyleCorporateBlue',
        nameFallback: 'Xtram Business',
        descriptionKey: 'DocumentStyleCorporateBlueDesc',
        descriptionFallback: 'פס כותרת כחול, טבלה נקייה וסה״כ מודגש בקופסה',
        previewAccent: '#1B3A6B',
        previewSecondary: '#A8C5E2',
        layout: 'corporateBand',
        tableHeaderMode: 'cleanLine',
        totalsMode: 'amountPill',
        defaults: {
            titleTextColor: '#1B3A6B',
            companyInfoTextColor: '#000000',
            clientDetailsTextColor: '#1a1a1a',
            tableTextColor: '#1a1a1a',
            totalsTextColor: '#1a1a1a',
            accentColor: '#1B3A6B',
            upperPanelColor: '#FFFFFF',
            textColor: '#1a1a1a',
            backgroundColor: '#ffffff',
            svgImageColor: '#1B3A6B',
            logoPosition: 'left',
            totalsLayout: 'vertical',
        },
        theme: {
            accent: '#1B3A6B',
            // Soft blue kept for table/meta accents only — header band default is white
            accentSoft: '#E8F0F8',
            title: '#2B5EA8',
            tableHeaderBg: 'transparent',
            tableHeaderColor: '#333333',
            zebraRow: '#ffffff',
            zebraRowAlt: '#ffffff',
            divider: '#1B3A6B',
            totalHighlightBg: '#1B3A6B',
            totalHighlightColor: '#ffffff',
            bannerBg: '#1B3A6B',
            bandBg: '#FFFFFF',
            topBarLight: '#A8C5E2',
            topBarDark: '#1B3A6B',
            itemsTitleBar: true,
            rowDivider: '#D0D0D0',
        },
    },
    modernBanner: {
        id: 'modernBanner',
        nameKey: 'DocumentStyleModernBanner',
        nameFallback: 'Xtram Modern',
        descriptionKey: 'DocumentStyleModernBannerDesc',
        descriptionFallback: 'כותרת כחולה מלאה עם פרטי עסק וסכום בולט',
        previewAccent: '#2B7DE9',
        previewSecondary: '#6BA3F0',
        layout: 'modernBanner',
        tableHeaderMode: 'accentText',
        totalsMode: 'stackRight',
        defaults: {
            // White only on the blue banner header — body/client stay dark
            titleTextColor: '#FFFFFF',
            companyInfoTextColor: '#FFFFFF',
            clientDetailsTextColor: '#222222',
            tableTextColor: '#222222',
            totalsTextColor: '#222222',
            accentColor: '#2B7DE9',
            upperPanelColor: '#2B7DE9',
            textColor: '#222222',
            backgroundColor: '#ffffff',
            svgImageColor: '#2B7DE9',
            logoPosition: 'left',
            totalsLayout: 'vertical',
        },
        theme: {
            accent: '#2B7DE9',
            accentSoft: '#e8f1fc',
            title: '#2B7DE9',
            headerRightColor: '#FFFFFF',
            tableHeaderBg: 'transparent',
            tableHeaderColor: '#2B7DE9',
            zebraRow: '#f7f9fc',
            divider: '#2B7DE9',
            totalHighlightBg: 'transparent',
            totalHighlightColor: '#2B7DE9',
            bannerBg: '#2B7DE9',
            bannerText: '#FFFFFF',
        },
    },
    softWaves: {
        id: 'softWaves',
        nameKey: 'DocumentStyleSoftWaves',
        nameFallback: 'Xtram Soft',
        descriptionKey: 'DocumentStyleSoftWavesDesc',
        descriptionFallback: 'כותרת גלית, פריסה אוורירית — מתאים לרקע תמונה',
        previewAccent: '#7EB8E0',
        previewSecondary: '#B8D9F0',
        layout: 'softWaves',
        tableHeaderMode: 'softBand',
        totalsMode: 'stackRight',
        defaults: {
            titleTextColor: '#3A5A7A',
            companyInfoTextColor: '#000000',
            clientDetailsTextColor: '#333333',
            tableTextColor: '#333333',
            totalsTextColor: '#333333',
            accentColor: '#7EB8E0',
            upperPanelColor: '#7EB8E0',
            textColor: '#333333',
            backgroundColor: '#ffffff',
            svgImageColor: '#7EB8E0',
            logoPosition: 'left',
            totalsLayout: 'vertical',
        },
        theme: {
            accent: '#7EB8E0',
            accentSoft: '#EAF4FB',
            title: '#3A5A7A',
            tableHeaderBg: '#EAF4FB',
            tableHeaderColor: '#5A8AB0',
            zebraRow: '#f9fbfd',
            divider: '#7EB8E0',
            totalHighlightBg: 'transparent',
            totalHighlightColor: '#3A5A7A',
            bannerBg: null,
            waveLight: '#B8D9F0',
            waveDark: '#7EB8E0',
        },
    },
    formalLines: {
        id: 'formalLines',
        nameKey: 'DocumentStyleFormalLines',
        nameFallback: 'Xtram Formal',
        descriptionKey: 'DocumentStyleFormalLinesDesc',
        descriptionFallback: 'חשבונית קלאסית בשחור-לבן עם מסגרות וקווים עבים',
        previewAccent: '#111111',
        previewSecondary: '#888888',
        layout: 'formalLines',
        tableHeaderMode: 'lineHeader',
        totalsMode: 'boxedTotal',
        defaults: {
            titleTextColor: '#000000',
            companyInfoTextColor: '#000000',
            clientDetailsTextColor: '#000000',
            tableTextColor: '#000000',
            totalsTextColor: '#000000',
            accentColor: '#111111',
            upperPanelColor: '#ffffff',
            textColor: '#000000',
            backgroundColor: '#ffffff',
            svgImageColor: '#000000',
            logoPosition: 'left',
            totalsLayout: 'vertical',
        },
        theme: {
            accent: '#111111',
            accentSoft: '#f5f5f5',
            title: '#000000',
            tableHeaderBg: 'transparent',
            tableHeaderColor: '#000000',
            zebraRow: '#ffffff',
            zebraRowAlt: '#ffffff',
            divider: '#111111',
            totalHighlightBg: '#ffffff',
            totalHighlightColor: '#000000',
            bannerBg: null,
            hideItemsTitle: true,
        },
    },
    tealModern: {
        id: 'tealModern',
        nameKey: 'DocumentStyleTealModern',
        nameFallback: 'Xtram Wow',
        descriptionKey: 'DocumentStyleTealModernDesc',
        descriptionFallback: 'עיצוב מודרני נקי עם כותרת טורקיז ותיבת סה״כ מודגשת',
        previewAccent: '#558B7F',
        previewSecondary: '#D5E6E2',
        layout: 'tealModern',
        tableHeaderMode: 'tealLine',
        totalsMode: 'tealDueBlock',
        defaults: {
            titleTextColor: '#558B7F',
            companyInfoTextColor: '#000000',
            clientDetailsTextColor: '#222222',
            tableTextColor: '#222222',
            totalsTextColor: '#222222',
            accentColor: '#558B7F',
            upperPanelColor: '#ffffff',
            textColor: '#222222',
            backgroundColor: '#ffffff',
            svgImageColor: '#558B7F',
            logoPosition: 'left',
            totalsLayout: 'vertical',
        },
        theme: {
            accent: '#558B7F',
            accentSoft: '#E8F2EF',
            title: '#558B7F',
            tableHeaderBg: 'transparent',
            tableHeaderColor: '#555555',
            zebraRow: '#ffffff',
            zebraRowAlt: '#ffffff',
            divider: '#D0D0D0',
            dottedDivider: '#558B7F',
            totalHighlightBg: '#558B7F',
            totalHighlightColor: '#ffffff',
            bannerBg: null,
            hideItemsTitle: true,
        },
    },
    navyCard: {
        id: 'navyCard',
        nameKey: 'DocumentStyleNavyCard',
        nameFallback: 'Xtram Bold',
        descriptionKey: 'DocumentStyleNavyCardDesc',
        descriptionFallback: 'כרטיס כותרת כחול כהה, טבלה אפורה וסה״כ מפוצל לשתי קופסאות',
        previewAccent: '#2d4d76',
        previewSecondary: '#6de1aa',
        layout: 'navyCard',
        tableHeaderMode: 'navyGrey',
        totalsMode: 'splitNavyDue',
        defaults: {
            titleTextColor: '#2d4d76',
            // Navy band uses white text in markup; company color picker stays dark for body contrast
            companyInfoTextColor: '#FFFFFF',
            clientDetailsTextColor: '#1a1a1a',
            tableTextColor: '#1a1a1a',
            totalsTextColor: '#1a1a1a',
            accentColor: '#2d4d76',
            upperPanelColor: '#2d4d76',
            textColor: '#1a1a1a',
            backgroundColor: '#ffffff',
            svgImageColor: '#2d4d76',
            logoPosition: 'left',
            totalsLayout: 'vertical',
        },
        theme: {
            accent: '#2d4d76',
            accentSoft: '#EDEDED',
            title: '#2d4d76',
            tableHeaderBg: 'transparent',
            tableHeaderColor: '#2d4d76',
            zebraRow: '#EDEDED',
            zebraRowAlt: '#EDEDED',
            divider: '#2d4d76',
            totalHighlightBg: '#2d4d76',
            totalHighlightColor: '#ffffff',
            bannerBg: '#2d4d76',
            bannerText: '#FFFFFF',
            mintAccent: '#6de1aa',
            rowDivider: '#ffffff',
            hideItemsTitle: true,
            fullBleedBody: true,
        },
    },
};

export const DOCUMENT_STYLE_LIST = Object.values(DOCUMENT_STYLE_VERSIONS);

export const DEFAULT_DOCUMENT_STYLE_ID = 'classic';

/** Keys that must reset when switching layout (prevents Modern white / old fonts leaking). */
const STYLE_COLOR_RESET_DEFAULTS = {
    documentFont: 'Arial',
    titleTextColor: '#000000',
    companyInfoTextColor: '#000000',
    clientDetailsTextColor: '#000000',
    tableTextColor: '#000000',
    totalsTextColor: '#000000',
    accentColor: '#304FFF',
    textColor: '#000000',
    backgroundColor: '#ffffff',
    upperPanelColor: '#fbfbfb',
};

/**
 * Resolve a style version by id (falls back to classic).
 */
export const resolveDocumentStyle = (styleId) => {
    if (styleId && DOCUMENT_STYLE_VERSIONS[styleId]) {
        return DOCUMENT_STYLE_VERSIONS[styleId];
    }
    return DOCUMENT_STYLE_VERSIONS[DEFAULT_DOCUMENT_STYLE_ID];
};

/**
 * Column settings each layout controls: switching layout resets them to that layout's values.
 * `headerDetails` (business header fields and shared rows) is kept per layout too; a layout
 * without its own header starts from the one currently shown.
 */
const STYLE_CONTROLLED_KEYS = [...new Set([
    ...Object.keys(STYLE_COLOR_RESET_DEFAULTS),
    ...DOCUMENT_STYLE_LIST.flatMap((style) => Object.keys(style.defaults || {})),
    'headerDetails',
])];

/** ColumnSettings key holding `{ [styleId]: { ...controlled keys } }` — each layout's last-used values. */
export const STYLE_CUSTOMIZATIONS_KEY = 'styleCustomizations';

const pickControlledKeys = (columnSettings) => STYLE_CONTROLLED_KEYS.reduce((acc, key) => {
    if (columnSettings[key] !== undefined && (key !== 'headerDetails' || columnSettings[key])) {
        acc[key] = columnSettings[key];
    }
    return acc;
}, {});

/**
 * Merge style defaults into column settings when user picks a version.
 * Resets section/page colors, the document font and other layout-controlled keys to the chosen
 * layout (no cross-layout leak), then restores what the user last set on that layout, so leaving
 * a layout and coming back keeps e.g. a vertical totals block. Logo/signature sizes are preserved
 * unless the style overrides them.
 */
export const applyStyleVersionToColumnSettings = (columnSettings = {}, styleId) => {
    const style = resolveDocumentStyle(styleId);
    const currentId = columnSettings.documentStyleVersion
        ? resolveDocumentStyle(columnSettings.documentStyleVersion).id
        : null;
    const customizations = { ...(columnSettings[STYLE_CUSTOMIZATIONS_KEY] || {}) };
    if (currentId) {
        customizations[currentId] = pickControlledKeys(columnSettings);
    }
    const next = {
        ...columnSettings,
        ...STYLE_COLOR_RESET_DEFAULTS,
        ...style.defaults,
        ...(customizations[style.id] || {}),
        documentStyleVersion: style.id,
        [STYLE_CUSTOMIZATIONS_KEY]: customizations,
    };
    // Guarantee Business header band is white (not legacy soft blue) unless the user chose one
    if (style.id === 'corporateBlue' && customizations[style.id]?.upperPanelColor === undefined) {
        next.upperPanelColor = style.defaults.upperPanelColor || '#FFFFFF';
    }
    return next;
};
